import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { templates, patterns, defaultPattern, getPatternStyle } from './templates';
import './styles/pages.css';

const CreateInvite = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const template = templates[templateId];

  const [question, setQuestion] = useState(template?.defaultQuestion || 'Will you be my Valentine?');
  const [cta, setCta] = useState("Yay, you won't regret this cause we are going for a romantic dinner at...");
  const [selectedPattern, setSelectedPattern] = useState(defaultPattern);
  const [step, setStep] = useState(1);

  if (!template) {
    navigate('/Playground/Inviteapp/templates');
    return null;
  }

  const handleNext = () => {
    if (step === 1 && question.trim()) {
      setStep(2);
    } else if (step === 2 && cta.trim()) {
      navigate('/Playground/Inviteapp/preview', {
        state: {
          templateId,
          question,
          cta,
          pattern: selectedPattern,
        },
      });
    }
  };

  const handleBack = () => {
    if (step === 1) {
      navigate('/Playground/Inviteapp/templates');
    } else {
      setStep(step - 1);
    }
  };

  return (
    <div
      className="create-page"
      style={{ background: template.colors.background }}
    >
      <div className="create-container">
        <motion.div
          className="step-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className={`step ${step >= 1 ? 'active' : ''}`}>Step 1. Will you?</div>
          <div className="step-line" />
          <div className={`step ${step >= 2 ? 'active' : ''}`}>Step 2. Only for you!</div>
        </motion.div>

        {step === 1 ? (
          <motion.div
            className="create-form"
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h2 style={{ color: template.colors.primary }}>
              Write Your Question {'\uD83D\uDCAD'}
            </h2>
            <p className="form-hint">
              This is what your special someone will see first!
            </p>

            <div className="preview-box" style={{ background: template.colors.background }}>
              <div
                className="pattern-overlay"
                style={getPatternStyle(selectedPattern, template.colors.primary)}
              />
              <img
                src={template.preview}
                alt={template.name}
                className="preview-image-small"
              />

              <p className="preview-question" style={{ color: template.colors.primary }}>
                {question || 'Your question here...'}
              </p>
            </div>

            <textarea
              className="invite-input"
              placeholder="e.g., Will you be my Valentine?"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={200}
              style={{ borderColor: template.colors.primary }}
            />
            <span className="char-count">{question.length}/200</span>

            <div className="pattern-selector">
              <h3 style={{ color: template.colors.primary }}>Choose a Background Pattern</h3>
              <div className="pattern-options">
                {Object.values(patterns).map((pattern) => (
                  <motion.div
                    key={pattern.id}
                    className={`pattern-option ${selectedPattern === pattern.id ? 'selected' : ''}`}
                    onClick={() => setSelectedPattern(pattern.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      background: template.colors.background,
                      borderColor: selectedPattern === pattern.id ? template.colors.primary : '#ddd',
                    }}
                  >
                    <div
                      className="pattern-overlay"
                      style={getPatternStyle(pattern.id, template.colors.primary)}
                    />
                    <span className="pattern-name">{pattern.name}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            className="create-form"
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h2 style={{ color: template.colors.primary }}>
              Add Your Reveal Message {'\uD83C\uDF89'}
            </h2>
            <p className="form-hint">
              This appears when they click "Yes"! Make it special!
            </p>

            <div className="preview-box cta-preview" style={{ background: template.colors.background }}>
              <img
                src={template.preview}
                alt={template.name}
                className="preview-image-small"
              />
              <p style={{ color: template.colors.primary }}>
                {cta || 'Your reveal message here...'}
              </p>
            </div>

            <textarea
              className="invite-input large"
              placeholder="e.g., Yay! Let's celebrate with dinner at our favorite restaurant..."
              value={cta}
              onChange={(e) => setCta(e.target.value)}
              maxLength={500}
              style={{ borderColor: template.colors.primary }}
            />
            <span className="char-count">{cta.length}/500</span>
          </motion.div>
        )}

        <div className="button-row">
          <button className="secondary-button" onClick={handleBack}>
            {'\u2190'} Back
          </button>
          <motion.button
            className="primary-button"
            onClick={handleNext}
            disabled={(step === 1 && !question.trim()) || (step === 2 && !cta.trim())}
            style={{
              background: `linear-gradient(135deg, ${template.colors.primary}, ${template.colors.secondary})`,
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {step === 2 ? 'Preview Invite \u2728' : 'Next \u2192'}
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default CreateInvite;
